// Choices persist across the theme's variant morphs, scoped to this product section.
const choices = new Map();

// Appstle retains inactive plans in Shopify allocations; its own widget hides them.
export function availablePlans(plans, inactiveIds) {
  const inactive = new Set(inactiveIds.map(String));
  return plans.filter((plan) => !inactive.has(String(plan.id)));
}

export function sortPlans(plans) {
  const weeks = (plan) => {
    const match = /^every\s+(\d+)\s+weeks?\s*$/i.exec(plan.name);
    return match ? Number(match[1]) : Infinity;
  };
  // Unrecognised labels retain their relative order after the weekly options.
  return [...plans].sort((a, b) => weeks(a) - weeks(b));
}

export function choosePlan(plans, choice) {
  if (choice?.kind === 'onetime' || !plans.length) return null;
  return plans.find((p) => p.id === choice?.planId)
    || plans.find((p) => p.name === choice?.name)
    || plans.find((p) => /every\s*2\s*weeks/i.test(p.name))
    || plans[0];
}

class PurchaseOptions extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.root = this.closest('.shopify-section');
    this.key = `${this.root?.id}:${this.dataset.productId}`;
    this.form = this.closest('form');
    if (!this.form || !this.root) return;
    if (!choices.has(this.key)) {
      const requestedPlan = new URL(window.location.href).searchParams.get('selling_plan');
      if (requestedPlan) choices.set(this.key, { kind: 'subscription', planId: requestedPlan });
    }
    this.addEventListener('click', (event) => {
      const card = event.target.closest('[data-cb-purchase-card]');
      if (!card || event.target.closest('select')) return;
      const select = this.querySelector('select');
      choices.set(this.key, {
        kind: card.dataset.cbPurchaseCard,
        planId: select.value,
        name: select.selectedOptions[0]?.textContent,
      });
      this.sync();
    }, { signal });
    this.addEventListener('change', (event) => {
      if (!event.target.matches('select')) return;
      choices.set(this.key, { kind: 'subscription', planId: event.target.value, name: event.target.selectedOptions[0]?.textContent });
      this.sync();
    }, { signal });
    this.root.addEventListener('variant:update', () => this.schedule(), { signal });
    this.root.addEventListener('cb:purchase-refresh', () => this.sync(), { signal });
    // Read the final variant and selection again before native FormData is built.
    this.form.addEventListener('submit', () => this.sync(), { capture: true, signal });
    this.form.addEventListener('formdata', (event) => {
      const plan = this.selectedPlan();
      if (plan) event.formData.set('selling_plan', plan.id);
      else event.formData.delete('selling_plan');
    }, { signal });
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(this.form, { childList: true, subtree: true });
    this.sync();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  schedule() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.isConnected && this.sync());
  }

  plans() {
    const variantId = this.form.querySelector('input[name="id"]')?.value;
    try {
      const plans = JSON.parse(this.querySelector('[data-cb-purchase-plans]').textContent)[variantId] || [];
      const inactiveIds = JSON.parse(this.querySelector('[data-cb-inactive-plans]')?.textContent || '[]');
      return availablePlans(plans, inactiveIds);
    }
    catch { return []; }
  }

  selectedPlan() { return choosePlan(this.plans(), choices.get(this.key)); }

  sync() {
    const plans = this.plans();
    const plan = choosePlan(plans, choices.get(this.key));
    const select = this.querySelector('select');
    const ordered = sortPlans(plans);
    const signature = JSON.stringify(ordered.map((p) => [p.id, p.name]));
    if (select.dataset.options !== signature) {
      select.replaceChildren(...ordered.map((p) => new Option(p.name, p.id)));
      select.dataset.options = signature;
    }
    if (plan) select.value = plan.id;
    this.hidden = !plans.length;
    for (const card of this.querySelectorAll('[data-cb-purchase-card]')) {
      const selected = card.dataset.cbPurchaseCard === (plan ? 'subscription' : 'onetime');
      card.dataset.selected = String(selected);
      card.querySelector('button').setAttribute('aria-pressed', String(selected));
    }
    let input = this.form.querySelector('input[name="selling_plan"]');
    if (plan) {
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        this.append(input);
      }
      if (input.value !== plan.id || input.getAttribute('value') !== plan.id) {
        input.value = plan.id;
        input.setAttribute('value', plan.id);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (input) {
      input.remove();
      this.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('cb-purchase-options')) customElements.define('cb-purchase-options', PurchaseOptions);
